import cloneDeep from "lodash/cloneDeep";
import {
    OperationOptions,
    Operations,
    ProblemData,
    ProblemFormatOptions,
    SingleDigitNumbers,
} from "../MathFactsGenerator/Constants.ts";
import { DefaultSettings, ISettingsProps } from "../MathFactsGenerator/Models.ts";
import { bucketFor } from "../MathFactsGenerator/FactRanges.ts";
import { GenerateDataV2 } from "../MathFactsGenerator/ProblemGenerator.tsx";
import { shuffle } from "../Shared/Helpers.tsx";
import { cardFaces } from "./cardFaces.ts";
import { CARDS_PER_SHEET } from "./PdfGenerator/cardLayout.ts";

/** How many cards the on-screen deck holds. There is no page to fill. */
export const DECK_SIZE = 20;

/**
 * The numbers a deck opens with: 1 to 9 on both sides. Zero is left out of the
 * default selection - "n x 0" and "0 ÷ 0" are not facts worth drilling - but it
 * stays in the range so the checkbox is there to switch on.
 */
const DEFAULT_DECK_NUMBERS = SingleDigitNumbers.Range.filter((n) => n !== 0);

/**
 * The settings a flash-card route opens with: one operation, math facts, both
 * operands drawn from the single-digit facts so the deck is varied without
 * anyone touching the range controls first.
 *
 * It is `ISettingsProps` rather than a model of its own because that is what
 * GenerateDataV2 takes - a separate shape would only have to be mapped back.
 */
export function flashCardSettings(operation: Operations): ISettingsProps {
    const settings = cloneDeep(DefaultSettings);
    settings.Format = ProblemFormatOptions.MathFacts;
    settings.AllOperations = false;
    settings.Page = operation;
    settings.Operators = OperationOptions.filter((op) => op.value === operation);
    settings.Remainders = false;
    // For printing: one sheet of cards, with the answer side on by default -
    // a set of flash cards with no answers on the back is half a set. Neither
    // field affects the on-screen deck, which builds its own single page.
    settings.NumberOfPages = 1;
    settings.AnswerPages = true;
    // The multiplication and division decks offer the 10s, 11s and 12s to tick
    // (ADR 0064); every deck opens on 1 to 9.
    const offered = bucketFor(1, [operation]).Range;
    settings.NumberRanges.forEach((range) => {
        range.NumberOfDigits = 1;
        range.NumberRange = [...offered];
        range.NumberRangeSelection = [...DEFAULT_DECK_NUMBERS];
    });
    return settings;
}

/**
 * A shuffled deck of problems for the current settings.
 *
 * GenerateDataV2 is non-deterministic, so callers must hold the result in state
 * and only rebuild it on a deliberate action (mount, a settings change, "new
 * deck") - never inline in render, or the deck reshuffles under the user on
 * every "next".
 */
export function buildDeck(settings: ISettingsProps): ProblemData[] {
    const pages = GenerateDataV2({
        ...cloneDeep(settings),
        NumberOfPages: 1,
        NumberOfProblemsPerPage: DECK_SIZE,
    });
    return pages?.[0] ?? [];
}

/**
 * Whether the settings can produce any problems at all - the same conditions
 * GenerateDataV2 returns null on. Lets a caller decide whether to offer a deck
 * or a quiz without calling the generator during render.
 */
export function canBuildDeck(settings: ISettingsProps): boolean {
    return (
        (settings.Operators?.length ?? 0) > 0 &&
        settings.NumberRanges.every((range) => range.NumberRangeSelection.length > 0)
    );
}

/**
 * How many rounds of generation to run before accepting that the settings
 * cannot produce any more different problems, and a hard stop in case they
 * somehow keep trickling in.
 */
const BARREN_ROUNDS = 5;
const MAX_ROUNDS = 25;


/**
 * Up to `wanted` different problems.
 *
 * The generator draws with replacement and only separates duplicates that land
 * next to each other, which is right for a worksheet - the same fact twice is
 * more practice - and wrong for a set of cards, where it is one card printed
 * twice and another fact missing.
 *
 * Rather than reimplement the generator's rules to enumerate the pool directly,
 * this keeps asking it for problems and keeps the ones it has not seen. Two
 * numbers are the same card when they print the same face, so `cardFaces` is
 * the identity - `3 x 4` and `4 x 3` are different cards, which is what they
 * are to a child learning them.
 *
 * The pool is finite and often small: single-digit multiplication has 81 facts,
 * and a narrow range can have one. When rounds stop turning up anything new the
 * pool is taken to be exhausted and the caller gets everything there was.
 */
function collectDistinctCards(settings: ISettingsProps, wanted: number): ProblemData[] {
    const found = new Map<string, ProblemData>();
    let barren = 0;

    for (let round = 0; round < MAX_ROUNDS && found.size < wanted && barren < BARREN_ROUNDS; round++) {
        const before = found.size;
        const pages = GenerateDataV2({
            ...cloneDeep(settings),
            NumberOfPages: 1,
            NumberOfProblemsPerPage: wanted,
        });

        for (const problem of pages?.[0] ?? []) {
            if (found.size >= wanted) {
                break;
            }
            const key = cardFaces(problem).front;
            if (!found.has(key)) {
                found.set(key, problem);
            }
        }

        barren = found.size === before ? barren + 1 : 0;
    }

    // Later rounds surface the problems the filters make rarest, so without a
    // shuffle those all land together on the last sheet.
    return shuffle([...found.values()]);
}

/**
 * Cards for printing, one array per sheet, with no card repeated.
 *
 * `settings.NumberOfPages` means sheets here. Asking for more cards than the
 * settings can produce returns the whole set and no more, so the last sheet may
 * be short - which the layout pads. The same non-determinism warning as
 * buildDeck applies: hold the result in state, do not call this during render.
 */
export function buildPrintableSheets(settings: ISettingsProps): ProblemData[][] {
    const wanted = CARDS_PER_SHEET * Math.max(1, settings.NumberOfPages);
    const cards = collectDistinctCards(settings, wanted);

    const sheets: ProblemData[][] = [];
    for (let start = 0; start < cards.length; start += CARDS_PER_SHEET) {
        sheets.push(cards.slice(start, start + CARDS_PER_SHEET));
    }
    return sheets;
}
